import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import type { CurrentUser } from '../firebase.ts';
import { Logo, ArrowDownIcon, ArrowRightIcon } from '../constants.tsx';
import AnimatedParticles from './AnimatedParticles.tsx';
import HolidayBanner from './HolidayBanner.tsx';

// --- DEBUG: Log module execution start ---
console.log('[Header.tsx] Module script loaded.');

interface HeaderProps {
    currentUser: CurrentUser | null;
    onSignInClick: () => void;
    onSignUpClick: () => void;
    onSignOutClick: () => void;
}

const LanguageToggle: React.FC = () => {
    const { t, i18n } = useTranslation();
    const handleLanguageToggle = () => {
        const newLang = i18n.language === 'en' ? 'ar' : 'en';
        // --- DEBUG: Log language toggle ---
        console.log(`[LanguageToggle] Changing language from ${i18n.language} to ${newLang}`);
        i18n.changeLanguage(newLang);
    };

    return (
        <button
            onClick={handleLanguageToggle}
            type="button"
            className="relative flex items-center h-8 w-16 cursor-pointer rounded-full bg-slate-700 p-1 transition-colors duration-300 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 focus:ring-offset-dark"
            role="switch"
            aria-checked={i18n.language === 'ar'}
            aria-label={t('toggleLanguage', 'Toggle Language')}
            dir="ltr"
        >
            <span className="sr-only">Toggle Language</span>
            <div className="absolute inset-0 flex items-center justify-around z-0">
                <span className="text-xs font-bold text-white/70">EN</span>
                <span className="text-xs font-bold text-white/70">AR</span>
            </div>
            <span
                className={`inline-block h-6 w-6 transform rounded-full bg-white shadow-md transition-transform duration-300 ease-in-out z-10
                ${i18n.language === 'ar' ? 'translate-x-8' : 'translate-x-0'}`}
            />
        </button>
    );
};

const AuthButtons: React.FC<Omit<HeaderProps, 'currentUser'> & { currentUser: CurrentUser | null }> = ({ currentUser, onSignInClick, onSignUpClick, onSignOutClick }) => {
    // --- DEBUG: Log AuthButtons render ---
    console.log('[AuthButtons] Rendering. Current user:', currentUser ? currentUser.auth.uid : 'null');
    const { t } = useTranslation();
    if (currentUser) {
        const displayName = currentUser.profile.displayName || currentUser.auth.email?.split('@')[0] || t('userProfile');
        return (
            <div className="flex items-center flex-nowrap gap-x-4 justify-center lg:justify-start whitespace-nowrap">
                <LanguageToggle />
                {currentUser.profile.scar && (
                    <a href="#/create-post" className="header-signup-btn text-sm font-medium flex items-center gap-2 py-2 px-4">
                        {t('createPost')}
                    </a>
                )}
                <span className="text-white text-sm font-medium">
                    {t('welcomeMessage', { name: displayName })}
                </span>
                <button onClick={onSignOutClick} className="header-signin-btn text-sm font-medium">{t('signOut')}</button>
            </div>
        )
    }
    return (
        <div className="flex items-center flex-nowrap gap-4 justify-center lg:justify-start whitespace-nowrap">
            <LanguageToggle />
            <button onClick={onSignInClick} className="header-signin-btn text-sm font-medium">{t('signIn')}</button>
            <button onClick={onSignUpClick} className="header-signup-btn text-sm font-medium flex items-center gap-2 py-2 px-4">
                {t('signUp')} <ArrowRightIcon />
            </button>
        </div>
    );
};

const SaudiClock: React.FC = () => {
    const [timeStr, setTimeStr] = useState('');

    useEffect(() => {
        const updateClock = () => {
            const now = new Date();
            const options: Intl.DateTimeFormatOptions = {
                timeZone: 'Asia/Riyadh',
                hour12: true,
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit'
            };
            const formatter = new Intl.DateTimeFormat('en-US', options);
            setTimeStr(formatter.format(now));
        };
        updateClock();
        const intv = setInterval(updateClock, 1000);
        return () => clearInterval(intv);
    }, []);

    return (
        <div className="flex items-center justify-center gap-2 text-white/80 bg-white/5 rounded-lg px-3 py-1.5 border border-white/10 font-mono text-sm" dir="ltr">
            <span>🇸🇦</span>
            <span>{timeStr}</span>
        </div>
    );
};

const ContactNumber: React.FC = () => {
    return (
        <a 
            href="tel:+971541988034" 
            className="flex items-center justify-center gap-2 text-white/80 bg-white/5 hover:bg-white/10 rounded-lg px-3 py-1.5 border border-white/10 font-mono text-sm whitespace-nowrap transition-colors"
            dir="ltr"
        >
            <svg className="w-4 h-4 text-white/60 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.94.725l.548 2.2a1 1 0 01-.321.988l-1.305.98a10.582 10.582 0 004.872 4.872l.98-1.305a1 1 0 01.988-.321l2.2.548a1 1 0 01.725.94V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"></path>
            </svg>
            <span>971-541988034</span>
        </a>
    );
};


const Header: React.FC<HeaderProps> = ({ currentUser, onSignInClick, onSignUpClick, onSignOutClick }) => {
    // --- DEBUG: Log component render ---
    console.log('[Header] Component rendering or re-rendering...');

    const { t } = useTranslation();
    const [stickyMenu, setStickyMenu] = useState(false);
    const [navigationOpen, setNavigationOpen] = useState(false);
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const [activePage, setActivePage] = useState('home');

    useEffect(() => {
        // --- DEBUG: Log effect for scroll listener ---
        console.log('[Header] useEffect (scroll) running to set up sticky menu listener.');
        const handleScroll = () => {
            const isSticky = window.scrollY > 0;
            if (isSticky !== stickyMenu) {
                console.log(`[Header] Sticky menu state changed to: ${isSticky}`);
                setStickyMenu(isSticky);
            }
        }
        window.addEventListener('scroll', handleScroll);
        return () => {
            console.log('[Header] useEffect (scroll) cleanup.');
            window.removeEventListener('scroll', handleScroll);
        }
    }, [stickyMenu]); // Dependency added to avoid stale closure on stickyMenu

    const navLinkClass = "header-nav-link text-sm xl:text-[13px] 2xl:text-sm";
    const activeNavLinkClass = "header-nav-link-active";

    const handleNavClick = (page: string, sectionId: string) => {
        console.log(`[Header] Navigating to page: ${page}, section: #${sectionId}`);
        setActivePage(page);
        setNavigationOpen(false);
        const element = document.getElementById(sectionId);
        if (element) {
            element.scrollIntoView({ behavior: 'smooth' });
        }
    };

    const NavLinks: React.FC<{ isMobile?: boolean }> = ({ isMobile }) => (
        <ul className={`flex lg:items-center gap-3 2xl:gap-5 whitespace-nowrap ${isMobile ? 'flex-col text-center text-lg' : 'flex-row'}`}>
            <li><a href="#home" onClick={() => handleNavClick('home', 'home')} className={`${navLinkClass} ${activePage === 'home' ? activeNavLinkClass : ''}`}>{t('navHome')}</a></li>
            <li><a href="#features" onClick={() => handleNavClick('features', 'features')} className={`${navLinkClass} ${activePage === 'features' ? activeNavLinkClass : ''}`}>{t('navFeatures')}</a></li>
            <li><a href="/market/" className={navLinkClass}>{t('navStocks', 'Stocks')}</a></li>
            <li><a href="/news/" className={navLinkClass}>{t('navNews', 'News')}</a></li>
            <li className={`group relative`} onMouseEnter={() => !isMobile && setDropdownOpen(true)} onMouseLeave={() => !isMobile && setDropdownOpen(false)} onClick={() => isMobile && setDropdownOpen(!dropdownOpen)}>
                <a href="#" className={`${navLinkClass} flex items-center justify-center lg:justify-start gap-1`} onClick={(e) => e.preventDefault()}>
                    {t('navPlatform')} <ArrowDownIcon />
                </a>
                <ul className={`lg:absolute top-full ltr:left-0 rtl:right-0 bg-dark/95 backdrop-blur-xl border border-white/10 p-2 rounded-xl shadow-2xl min-w-[160px] ${dropdownOpen ? 'block' : 'hidden'} ${isMobile ? 'relative !bg-transparent !shadow-none !p-0 !pt-3 ltr:!pl-4 rtl:!pr-4' : 'lg:group-hover:block'}`}>
                    <li><a href="/about/" onClick={() => setNavigationOpen(false)} className="block text-sm text-white/70 hover:text-white py-2 px-3 rounded-lg hover:bg-white/5 transition-colors">{t('pageAbout')}</a></li>
                    <li><a href="/news/" onClick={() => setNavigationOpen(false)} className="block text-sm text-white/70 hover:text-white py-2 px-3 rounded-lg hover:bg-white/5 transition-colors">{t('pageBlog')}</a></li>
                </ul>
            </li>
            <li><a href="#support" onClick={() => handleNavClick('support', 'support')} className={`${navLinkClass} ${activePage === 'support' ? activeNavLinkClass : ''}`}>{t('navSupport')}</a></li>
        </ul>
    );

    {/* Standalone Highlighted Ask Sara Button */}
    const SaraHighlightedButton: React.FC = () => (
        <a
            href="#/chat"
            onClick={() => { setActivePage('chat'); setNavigationOpen(false); }}
            className="group relative inline-flex items-center gap-2 px-3 py-1.5 2xl:px-4 2xl:py-2 rounded-full bg-gradient-to-r from-purple-600 via-fuchsia-600 to-indigo-600 text-white font-bold text-xs 2xl:text-sm shadow-[0_0_20px_rgba(168,85,247,0.45)] hover:shadow-[0_0_30px_rgba(168,85,247,0.8)] hover:scale-105 transition-all duration-300 border border-purple-300/40 flex-shrink-0 select-none whitespace-nowrap"
            title="تحدث مع مستشارة الذكاء الاصطناعي سارة"
        >
            <span className="hidden 2xl:inline-block w-2 h-2 rounded-full bg-emerald-400 animate-ping"></span>
            <span className="text-sm 2xl:text-base">✨</span>
            <span className="tracking-wide">اسأل سارة</span>
            <span className="hidden 2xl:inline bg-white/20 text-[10px] px-1.5 py-0.5 rounded-full font-mono font-bold">AI</span>
        </a>
    );

    const DashboardButton: React.FC = () => (
        <a href="https://dash.tad-ai.com" target="_blank" rel="noopener noreferrer" className="header-dashboard-btn flex-shrink-0">
            <span className="header-dashboard-btn-inner whitespace-nowrap px-3.5 py-1.5 text-xs xl:text-sm">
                <AnimatedParticles count={10} />
                <span className="relative z-10">{t('navDashboard')}</span>
            </span>
        </a>
    );

    useEffect(() => {
        if (!navigationOpen) return;
        const previous = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => {
            document.body.style.overflow = previous;
        };
    }, [navigationOpen]);

    return (
        <>
        <header className={`fixed left-0 top-0 w-full z-[100] flex flex-col transition-all duration-300 ${stickyMenu ? 'bg-dark/85 backdrop-blur-xl shadow-xl border-b border-white/5' : 'bg-dark/40 backdrop-blur-md'}`}>
            <HolidayBanner />
            <div className="w-full max-w-[1600px] mx-auto px-3 sm:px-6 xl:px-8 flex items-center justify-between gap-3 relative py-3 lg:py-3.5">
                <div className="flex items-center gap-x-4 xl:gap-x-5 2xl:gap-x-7 min-w-0">
                    <a href="#home" onClick={() => handleNavClick('home', 'home')} className="flex-shrink-0">
                        <Logo />
                    </a>
                    <nav className="hidden xl:block min-w-0">
                        <NavLinks />
                    </nav>
                </div>
                <div className="flex items-center gap-2 2xl:gap-3 flex-nowrap flex-shrink-0">
                    {/* Ask Sara Glowing Special Button */}
                    <div className="hidden sm:block flex-shrink-0">
                        <SaraHighlightedButton />
                    </div>

                    <div className="hidden lg:flex items-center gap-2 2xl:gap-3 flex-nowrap">
                        <DashboardButton />
                        <AuthButtons
                            currentUser={currentUser}
                            onSignInClick={onSignInClick}
                            onSignUpClick={onSignUpClick}
                            onSignOutClick={onSignOutClick}
                        />
                    </div>

                    {/* Mobile Menu Button */}
                    <button
                        onClick={() => setNavigationOpen(!navigationOpen)}
                        className="xl:hidden w-10 h-10 flex items-center justify-center rounded-xl bg-white/5 hover:bg-white/10 transition-colors z-50 border border-white/10"
                        aria-label="Toggle navigation"
                    >
                        <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d={navigationOpen ? "M6 18L18 6M6 6l12 12" : "M4 6h16M4 12h16m-7 6h7"}></path>
                        </svg>
                    </button>
                </div>
            </div>
        </header>

            {/* Mobile Menu Panel — خارج الهيدر حتى لا يحصره backdrop-filter في ارتفاع الشريط */}
            <div className={`xl:hidden fixed inset-0 z-[90] bg-dark/95 backdrop-blur-2xl transition-transform duration-300 ease-in-out ${navigationOpen ? 'translate-x-0' : 'ltr:translate-x-full rtl:-translate-x-full'}`}>
                <div className="flex flex-col h-full p-6 sm:p-8 justify-between overflow-y-auto">
                    {/* Top: Navigation */}
                    <nav className="flex flex-col items-center gap-y-4 pt-14">
                        <div className="mb-2">
                            <SaraHighlightedButton />
                        </div>
                        <NavLinks isMobile />
                        <div className="mt-4 flex flex-col items-center justify-center gap-3 w-full max-w-xs">
                            <SaudiClock />
                            <ContactNumber />
                            <DashboardButton />
                        </div>
                    </nav>

                    {/* Bottom: Auth & Actions */}
                    <div className="w-full">
                        {!currentUser ? (
                            // Logged OUT view
                            <div className="flex flex-col items-center gap-4">
                                <button onClick={() => { onSignUpClick(); setNavigationOpen(false); }} className="header-signup-btn text-base font-medium flex items-center justify-center gap-2 py-3 w-full max-w-xs mx-auto">
                                    {t('signUp')} <ArrowRightIcon />
                                </button>
                                <button onClick={() => { onSignInClick(); setNavigationOpen(false); }} className="header-signin-btn text-base font-medium">{t('signIn')}</button>
                                <div className="mt-6">
                                    <LanguageToggle />
                                </div>
                            </div>
                        ) : (
                            // Logged IN view
                            <div className="flex flex-col items-center gap-4">
                                {currentUser.profile.scar && (
                                    <a href="#/create-post" onClick={() => setNavigationOpen(false)} className="header-signup-btn text-base font-medium flex items-center justify-center gap-2 py-3 w-full max-w-xs mx-auto">
                                        {t('createPost')}
                                    </a>
                                )}
                                <div className="text-center">
                                    <span className="text-white text-base font-medium block">
                                        {t('welcomeMessage', { name: currentUser.profile.displayName || currentUser.auth.email?.split('@')[0] || t('userProfile') })}
                                    </span>
                                    <button onClick={() => { onSignOutClick(); setNavigationOpen(false); }} className="header-signin-btn text-sm font-medium mt-2">{t('signOut')}</button>
                                </div>
                                <div className="mt-6">
                                    <LanguageToggle />
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </>
    );
};

export default Header;